/**
 * Helpers for working with structured screenplay data (v41+).
 * Converts structured DB records into formats consumed by ScriptPanel and TTS.
 */

import type { ScreenplayData, ScreenplayDialogue, ScreenplayAction, ScreenplayTransitionElement } from '@/types/timeline.ts';
import type { DialogLine } from './screenplayParser.ts';

// ─── Ordered elements (for ScriptPanel rendering) ────────────────────

export type ScreenplayElement =
  | { type: 'dialogue'; data: ScreenplayDialogue }
  | { type: 'action'; data: ScreenplayAction }
  | { type: 'transition'; data: ScreenplayTransitionElement };

/**
 * Merge dialogue, actions, and transitions into a single array
 * sorted by scriptOrder for rendering in script order.
 */
export function getOrderedElements(screenplay: ScreenplayData): ScreenplayElement[] {
  const elements: ScreenplayElement[] = [];

  for (const d of screenplay.dialogue) {
    elements.push({ type: 'dialogue', data: d });
  }
  for (const a of screenplay.actions) {
    elements.push({ type: 'action', data: a });
  }
  for (const t of screenplay.transitions) {
    elements.push({ type: 'transition', data: t });
  }

  elements.sort((a, b) => a.data.scriptOrder - b.data.scriptOrder);
  return elements;
}

// ─── Structured → DialogLine[] (for TTS pipeline) ───────────────────

/**
 * Convert structured ScreenplayDialogue records into the DialogLine format
 * expected by the TTS pipeline (same shape as parseScreenplayDialog output).
 */
export function structuredToDialogLines(screenplay: ScreenplayData): DialogLine[] {
  return screenplay.dialogue
    .slice()
    .sort((a, b) => a.scriptOrder - b.scriptOrder)
    .map((d, i) => ({
      character: d.characterName,
      text: d.dialogueText,
      parenthetical: d.parenthetical || undefined,
      lineIndex: i,
    }));
}

// ─── Structured → Plain text (for inline editing) ────────────────────

/**
 * Convert structured screenplay elements into FDX-indented plain text
 * suitable for editing in a textarea.
 */
export function screenplayToPlainText(screenplay: ScreenplayData): string {
  const elements = getOrderedElements(screenplay);
  const lines: string[] = [];

  for (const el of elements) {
    switch (el.type) {
      case 'dialogue': {
        const d = el.data;
        const nameDisplay = d.extension
          ? `${d.characterName} (${d.extension})`
          : d.characterName;
        lines.push('');
        lines.push('                 ' + nameDisplay.toUpperCase());
        if (d.parenthetical) {
          lines.push('            ' + d.parenthetical);
        }
        lines.push('       ' + d.dialogueText);
        break;
      }
      case 'action':
        lines.push('');
        lines.push(el.data.actionText);
        break;
      case 'transition':
        lines.push('');
        lines.push('                                        ' + el.data.transitionText.toUpperCase());
        break;
    }
  }

  return lines.join('\n').trim();
}

// ─── Character extraction ───────────────────────────────────────────

/**
 * Extract unique character names from structured dialogue
 * in first-appearance order (by scriptOrder).
 * Same contract as extractCharacters() from screenplayParser.ts.
 */
export function extractStructuredCharacters(screenplay: ScreenplayData): string[] {
  const sorted = screenplay.dialogue
    .slice()
    .sort((a, b) => a.scriptOrder - b.scriptOrder);

  const seen = new Set<string>();
  const result: string[] = [];
  for (const d of sorted) {
    if (d.characterName && !seen.has(d.characterName)) {
      seen.add(d.characterName);
      result.push(d.characterName);
    }
  }
  return result;
}
